'use client';

/* ──────────────────────────────────────────────
 *  TunnelSummary — encart « Votre sélection » partagé
 *  entre les tunnels réservation, carte cadeau et course.
 *  Purement présentationnel et piloté par props (aucun
 *  contexte) : chaque tunnel l'alimente via un adaptateur.
 *  Tous les prix/durées arrivent déjà formatés serveur.
 *  Rend la sidebar sticky du layout `wide` (`.bt-summary*`)
 *  ou le contenu de la feuille de `TunnelFooterBar`.
 * ────────────────────────────────────────────── */

import type { JSX, ReactNode } from 'react';

export interface TunnelSummarySupplement {
  name: string;
  /** Prix du supplément sur la ligne (total des quantités), déjà formaté serveur. */
  price?: string;
  /** Nombre de ce supplément sur la ligne : affiché « × n » au-delà d'un seul. */
  quantity?: number;
}

export interface TunnelSummaryItem {
  label: string;
  /** Offre qui remise la ligne (badge) : la ligne garde son prix normal. */
  badge?: string;
  /** Prix de la ligne, déjà formaté serveur (absent = pas de prix affiché). */
  price?: string;
  /** Sous-lignes informatives (ex. prestations incluses d'un forfait). */
  sublines?: string[];
  /** Suppléments sélectionnés (nom, prix formaté serveur optionnel, quantité au-delà d'une unité). */
  supplements?: TunnelSummarySupplement[];
  /** Si défini, affiche le bouton « × » de retrait. */
  onRemove?: () => void;
}

export interface TunnelSummaryContextRow {
  /** Emoji/pictogramme optionnel préfixé au texte. */
  icon?: string;
  text: string;
  /** Lien « modifier » de la ligne (retour à l'étape qui l'a choisie). */
  onEdit?: () => void;
  editLabel?: string;
  editAriaLabel?: string;
}

export interface TunnelSummaryPaymentRow {
  label: string;
  value: string;
  /** Ligne mise en avant (ex. reste à payer sur place). */
  highlight?: boolean;
}

export interface TunnelSummaryCta {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Affiche un spinner à la place du label (soumission en cours). */
  loading?: boolean;
}

export interface TunnelSummaryBackAction {
  label: string;
  onClick: () => void;
}

/** Message à la place du total (ex. récap en échec), avec son action (« Réessayer »). */
export interface TunnelSummaryNotice {
  text: string;
  actionLabel: string;
  onAction: () => void;
  /** L'action est en cours : spinner à la place de son libellé. */
  pending?: boolean;
}

export interface TunnelSummaryLabels {
  selection_title: string;
  total_label: string;
  /** Aria-label du bouton « × » de retrait — requis uniquement si des items ont `onRemove`. */
  remove?: string;
}

export interface TunnelSummaryProps {
  items: TunnelSummaryItem[];
  contextRows?: TunnelSummaryContextRow[];
  /** Total déjà résolu (peut être '' ou '…' pendant le chargement). */
  totalValue: string;
  /** Total au prix normal, barré devant `totalValue` quand une remise s'applique. */
  totalStruck?: string;
  totalDuration?: string;
  /** Rendu à la place de la ligne Total : le total connu serait faux (récap du panier en échec). */
  totalNotice?: TunnelSummaryNotice;
  /** Lignes de remise, juste au-dessus du total (montants signés serveur). */
  discountRows?: TunnelSummaryPaymentRow[];
  /** Rendu sous le total (champ « Code promo ou carte cadeau » du tunnel de réservation). */
  codeSlot?: ReactNode;
  /** Bons et cartes cadeaux, sous le champ code (montants signés serveur). */
  voucherRows?: TunnelSummaryPaymentRow[];
  paymentRows?: TunnelSummaryPaymentRow[];
  /** Action principale de l'étape, rendue dans la sidebar (layout `wide`). */
  cta?: TunnelSummaryCta;
  /** Lien retour rendu sous le CTA dans la sidebar (layout `wide`). */
  backAction?: TunnelSummaryBackAction;
  labels: TunnelSummaryLabels;
  /**
   * - `sidebar` : sidebar du layout `wide`. Sans élément, elle garde le titre et les
   *   actions (l'action de l'étape reste atteignable, la colonne ne saute pas).
   * - `sheet` : contenu seul (lignes + contexte + total), sans titre ni CTA —
   *   destiné à la feuille dépliée par `TunnelFooterBar` en colonne unique, qui
   *   possède l'état d'ouverture et rend son propre en-tête ; repris par la carte
   *   récap et par le récap de la confirmation.
   */
  variant: 'sidebar' | 'sheet';
}

export function TunnelSummary({
  items,
  contextRows,
  totalValue,
  totalStruck,
  totalDuration,
  totalNotice,
  discountRows,
  codeSlot,
  voucherRows,
  paymentRows,
  cta,
  backAction,
  labels,
  variant,
}: TunnelSummaryProps): JSX.Element | null {
  const hasItems = items.length > 0;
  if (!hasItems && (variant === 'sheet' || (!cta && !backAction))) return null;

  const renderItems = (): ReactNode =>
    items.map(
      (item, i): ReactNode => (
        <div key={`item-${i}`} className="bt-summary__service">
          <div className="bt-summary__row">
            <span className="bt-summary__row-label">
              {item.label}
              {item.badge && <span className="bt-summary__badge">{item.badge}</span>}
            </span>
            {(item.price || item.onRemove) && (
              <div className="bt-summary__row-actions">
                {item.price && <span className="bt-summary__row-value">{item.price}</span>}
                {item.onRemove && (
                  <button
                    type="button"
                    className="bt-summary__remove"
                    onClick={item.onRemove}
                    aria-label={labels.remove}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>
          {(item.sublines ?? []).map(
            (s, j): ReactNode => (
              <div key={`subline-${j}`} className="bt-summary__row">
                <span className="bt-summary__row-label bt-summary__row-label--sub">{s}</span>
              </div>
            ),
          )}
          {(item.supplements ?? []).map(
            (s, j): ReactNode => (
              <div key={`supp-${j}`} className="bt-summary__row">
                <span className="bt-summary__row-label bt-summary__row-label--sub">
                  + {s.name}
                  {s.quantity !== undefined && s.quantity > 1 ? ` × ${s.quantity}` : ''}
                </span>
                {s.price && <span className="bt-summary__row-value bt-summary__row-value--sub">{s.price}</span>}
              </div>
            ),
          )}
        </div>
      ),
    );

  const renderContext = (): ReactNode =>
    contextRows && contextRows.length > 0 ? (
      <>
        <div className="bt-summary__divider" />
        {contextRows.map(
          (row, i): ReactNode => (
            <div key={`ctx-${i}`} className="bt-summary__row">
              <span className="bt-summary__row-label bt-summary__row-label--sub">
                {row.icon ? `${row.icon} ` : ''}
                {row.text}
              </span>
              {row.onEdit && row.editLabel ? (
                <button
                  type="button"
                  className="bt-link-subtle bt-summary__edit"
                  onClick={row.onEdit}
                  aria-label={row.editAriaLabel}
                >
                  {row.editLabel}
                </button>
              ) : null}
            </div>
          ),
        )}
      </>
    ) : null;

  const renderAmountRows = (rows: TunnelSummaryPaymentRow[] | undefined, modifier: string): ReactNode =>
    (rows ?? []).map(
      (row, i): ReactNode => (
        <div key={`${modifier}-${i}`} className={`bt-summary__row bt-summary__row--${modifier}`}>
          <span className="bt-summary__row-label">{row.label}</span>
          <span className="bt-summary__row-value">{row.value}</span>
        </div>
      ),
    );

  const renderTotal = (): ReactNode => (
    <>
      <div className="bt-summary__divider" />
      {renderAmountRows(discountRows, 'discount')}
      {totalNotice ? (
        <div className="bt-error" role="alert">
          <p className="bt-error__message">{totalNotice.text}</p>
          <button
            type="button"
            className="bt-link-subtle"
            onClick={totalNotice.onAction}
            disabled={totalNotice.pending}
          >
            {totalNotice.pending ? <span className="bt-spinner bt-spinner--inline" /> : totalNotice.actionLabel}
          </button>
        </div>
      ) : (
        <div className="bt-summary__total">
          <span className="bt-summary__total-label">{labels.total_label}</span>
          <span className="bt-summary__total-value">
            {totalStruck && <s className="bt-summary__total-struck">{totalStruck}</s>}
            {totalValue}
          </span>
        </div>
      )}
      {totalDuration && (
        <div className="bt-summary__row">
          <span className="bt-summary__row-label bt-summary__row-label--sub">{totalDuration}</span>
        </div>
      )}
      {codeSlot}
      {renderAmountRows(voucherRows, 'voucher')}
      {(paymentRows ?? []).map(
        (row, i): ReactNode => (
          <div
            key={`pay-${i}`}
            className={row.highlight ? 'bt-summary__row bt-summary__row--highlight' : 'bt-summary__row'}
          >
            <span className="bt-summary__row-label bt-summary__row-label--sub">{row.label}</span>
            <span
              className={row.highlight ? 'bt-summary__row-value' : 'bt-summary__row-value bt-summary__row-value--sub'}
            >
              {row.value}
            </span>
          </div>
        ),
      )}
    </>
  );

  /* ── Contenu de la feuille du footer (colonne unique) ── */
  if (variant === 'sheet') {
    return (
      <div className="bt-footer-sheet__content">
        {renderItems()}
        {renderContext()}
        {renderTotal()}
      </div>
    );
  }

  return (
    <aside className="bt-summary">
      <div className="bt-summary__card">
        <div className="bt-summary__title">{labels.selection_title}</div>
        {hasItems && (
          <>
            {renderItems()}
            {renderContext()}
            {renderTotal()}
          </>
        )}
        {cta && (
          <button type="button" className="bt-cta" onClick={cta.onClick} disabled={cta.disabled || cta.loading}>
            {cta.loading ? <span className="bt-spinner bt-spinner--inline" /> : cta.label}
          </button>
        )}
        {backAction && (
          <button
            type="button"
            className="bt-link-subtle bt-summary__back-link"
            onClick={backAction.onClick}
          >{`← ${backAction.label}`}</button>
        )}
      </div>
    </aside>
  );
}
